import ProductionStorage from './productionStorage';
import { Platform } from 'react-native';

export interface TrialData {
  startDate: string;
  endDate: string;
  daysLeft: number;
  isActive: boolean;
  isExpired: boolean;
}

export class TrialManager {
  private static readonly TRIAL_DURATION_DAYS = 30;
  private static readonly TRIAL_STORAGE_KEY = 'bookapt_trial_data';

  static async startTrial(): Promise<TrialData> {
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(startDate.getDate() + this.TRIAL_DURATION_DAYS);

    const trialData: TrialData = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      daysLeft: this.TRIAL_DURATION_DAYS,
      isActive: true,
      isExpired: false,
    };

    try {
      // Save to local storage (for offline access)
      await ProductionStorage.setItem(this.TRIAL_STORAGE_KEY, JSON.stringify(trialData));
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          const { getFirestoreFunctions } = require('../services/unifiedAuthService');
          const { getApps, initializeApp } = require('firebase/app');
          const { firebaseConfig } = require('../config/firebaseConfig');
          const { AuthManager } = await import('./authManager');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            console.warn('⚠️  TrialManager: Firestore module unavailable, skipping Firestore save');
            return trialData;
          }
          const { doc, setDoc, getFirestore } = firestoreMod;
          const app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig);
          const db = getFirestore(app);
          
          if (db) {
            const user = await AuthManager.getSavedUser();
            const businessOwner = await AuthManager.getSavedBusinessOwner();
            const userId = user?.id || businessOwner?.id;
            
            if (userId) {
              await setDoc(doc(db, 'users', userId, 'trial_data', 'current'), {
                ...trialData,
                updatedAt: new Date().toISOString(),
              });
              console.log('✅ TrialManager: Trial data saved to Firestore');
            }
          }
        } catch (firestoreError: any) {
          console.error('❌ TrialManager: Error saving trial data to Firestore:', firestoreError);
          console.warn('⚠️  Trial data saved locally but not in Firestore');
          // Don't throw - local save succeeded
        }
      }
      
      console.log('🎉 Trial started:', trialData);
      return trialData;
    } catch (error) {
      console.error('Error starting trial:', error);
      return trialData;
    }
  }

  static async getTrialData(): Promise<TrialData | null> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedTrialData: TrialData | null = null;
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          const { getFirestoreFunctions } = require('../services/unifiedAuthService');
          const { getApps, initializeApp } = require('firebase/app');
          const { firebaseConfig } = require('../config/firebaseConfig');
          const { AuthManager } = await import('./authManager');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            throw new Error('Firestore module unavailable');
          }
          const { doc, getDoc, getFirestore } = firestoreMod;
          const app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig);
          const db = getFirestore(app);
          
          if (db) {
            const user = await AuthManager.getSavedUser();
            const businessOwner = await AuthManager.getSavedBusinessOwner();
            const userId = user?.id || businessOwner?.id;
            
            if (userId) {
              const trialDoc = await getDoc(doc(db, 'users', userId, 'trial_data', 'current'));
              if (trialDoc.exists()) {
                loadedTrialData = trialDoc.data() as TrialData;
                loadedFromFirestore = true;
                console.log(`✅ TrialManager: Loaded trial data from Firestore`);
                
                // Sync to local storage
                await ProductionStorage.setItem(this.TRIAL_STORAGE_KEY, JSON.stringify(loadedTrialData));
              }
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  TrialManager: Could not load trial data from Firestore, trying local storage:', firestoreError);
        }
      }
      
      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
      const trialDataString = await ProductionStorage.getItem(this.TRIAL_STORAGE_KEY);
        if (trialDataString) {
          loadedTrialData = JSON.parse(trialDataString);
          console.log(`📋 TrialManager: Loaded trial data from local storage`);
        } else {
          return null;
        }
      }
      
      if (!loadedTrialData) {
        return null;
      }
      
      // Check if trial has expired
      const now = new Date();
      const endDate = new Date(loadedTrialData.endDate);
      const isExpired = now > endDate;
      
      if (isExpired && loadedTrialData.isActive) {
        // Update trial status to expired
        const updatedTrialData: TrialData = {
          ...loadedTrialData,
          isActive: false,
          isExpired: true,
          daysLeft: 0,
        };
        
        // Update in both local storage and Firestore
        await ProductionStorage.setItem(this.TRIAL_STORAGE_KEY, JSON.stringify(updatedTrialData));
        
        if (Platform.OS === 'web') {
          this.saveTrialDataToFirestore(updatedTrialData).catch(err => {
            console.error('Error updating trial data in Firestore:', err);
          });
        }
        
        return updatedTrialData;
      }

      // Calculate remaining days
      const timeDiff = endDate.getTime() - now.getTime();
      const daysLeft = Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24)));
      
      const updatedTrialData: TrialData = {
        ...loadedTrialData,
        daysLeft,
        isExpired,
        isActive: !isExpired,
      };

      // Update with current days left in both local storage and Firestore
      await ProductionStorage.setItem(this.TRIAL_STORAGE_KEY, JSON.stringify(updatedTrialData));
      
      if (Platform.OS === 'web') {
        this.saveTrialDataToFirestore(updatedTrialData).catch(err => {
          console.error('Error updating trial data in Firestore:', err);
        });
      }
      
      return updatedTrialData;
    } catch (error) {
      console.error('Error getting trial data:', error);
      return null;
    }
  }
  
  // Save trial data to Firestore
  private static async saveTrialDataToFirestore(trialData: TrialData): Promise<void> {
    try {
      if (Platform.OS !== 'web') return;
      
      const { getFirestoreFunctions } = require('../services/unifiedAuthService');
      const { getApps, initializeApp } = require('firebase/app');
      const { firebaseConfig } = require('../config/firebaseConfig');
      const { AuthManager } = await import('./authManager');
      const firestoreMod = getFirestoreFunctions?.();
      if (!firestoreMod) {
        return;
      }
      const { doc, setDoc, getFirestore } = firestoreMod;
      const app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig);
      const db = getFirestore(app);
      
      if (db) {
        const user = await AuthManager.getSavedUser();
        const businessOwner = await AuthManager.getSavedBusinessOwner();
        const userId = user?.id || businessOwner?.id;
        
        if (userId) {
          await setDoc(doc(db, 'users', userId, 'trial_data', 'current'), {
            ...trialData,
            updatedAt: new Date().toISOString(),
          });
          console.log(`✅ TrialManager: Trial data saved to Firestore`);
        }
      }
    } catch (error) {
      console.error('Error saving trial data to Firestore:', error);
      // Don't throw - this is a sync operation
    }
  }

  static async isTrialExpired(): Promise<boolean> {
    const trialData = await this.getTrialData();
    return trialData ? trialData.isExpired : false;
  }

  static async isTrialActive(): Promise<boolean> {
    const trialData = await this.getTrialData();
    return trialData ? trialData.isActive : false;
  }

  static async getDaysLeft(): Promise<number> {
    const trialData = await this.getTrialData();
    return trialData ? trialData.daysLeft : 0;
  }

  static async extendTrial(days: number): Promise<TrialData | null> {
    try {
      const currentTrial = await this.getTrialData();
      if (!currentTrial) {
        return null;
      }

      const newEndDate = new Date(currentTrial.endDate);
      newEndDate.setDate(newEndDate.getDate() + days);

      const updatedTrialData: TrialData = {
        ...currentTrial,
        endDate: newEndDate.toISOString(),
        daysLeft: currentTrial.daysLeft + days,
        isActive: true,
        isExpired: false,
      };

      await ProductionStorage.setItem(this.TRIAL_STORAGE_KEY, JSON.stringify(updatedTrialData));
      
      console.log(`🎉 Trial extended by ${days} days`);
      return updatedTrialData;
    } catch (error) {
      console.error('Error extending trial:', error);
      return null;
    }
  }

  static async upgradeToPaid(planType: 'free' | 'starter' | 'professional' | 'business'): Promise<boolean> {
    try {
      // In a real app, this would integrate with payment processing
      console.log(`💳 Upgrading to ${planType} plan`);
      
      const subscriptionData = {
        planType,
        startDate: new Date().toISOString(),
        isActive: true,
      };

      // Save to local storage (for offline access)
      await ProductionStorage.setItem('bookapt_subscription', JSON.stringify(subscriptionData));

      // Remove trial data from local storage
      await ProductionStorage.removeItem(this.TRIAL_STORAGE_KEY);
      
      // Also save subscription to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          const { getFirestoreFunctions } = require('../services/unifiedAuthService');
          const { getApps, initializeApp } = require('firebase/app');
          const { firebaseConfig } = require('../config/firebaseConfig');
          const { AuthManager } = await import('./authManager');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            console.warn('⚠️  TrialManager: Firestore module unavailable, skipping Firestore save');
            return true;
          }
          const { doc, setDoc, getFirestore } = firestoreMod;
          const app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig);
          const db = getFirestore(app);
          
          if (db) {
            const user = await AuthManager.getSavedUser();
            const businessOwner = await AuthManager.getSavedBusinessOwner();
            const userId = user?.id || businessOwner?.id;
            
            if (userId) {
              // Save subscription data
              await setDoc(doc(db, 'users', userId, 'subscription', 'current'), {
                ...subscriptionData,
                updatedAt: new Date().toISOString(),
              });
              
              // Remove trial data from Firestore
              try {
                const { deleteDoc } = firestoreMod;
                await deleteDoc(doc(db, 'users', userId, 'trial_data', 'current'));
              } catch (e) {
                // Ignore if trial data doesn't exist
              }
              
              console.log(`✅ TrialManager: Subscription saved to Firestore`);
            }
          }
        } catch (firestoreError: any) {
          console.error('❌ TrialManager: Error saving subscription to Firestore:', firestoreError);
          console.warn('⚠️  Subscription saved locally but not in Firestore');
          // Don't throw - local save succeeded
        }
      }
      
      console.log(`✅ Successfully upgraded to ${planType} plan`);
      return true;
    } catch (error) {
      console.error('Error upgrading to paid plan:', error);
      return false;
    }
  }

  static async isSubscribed(): Promise<boolean> {
    try {
      const subscriptionData = await ProductionStorage.getItem('bookapt_subscription');
      return !!subscriptionData;
    } catch (error) {
      console.error('Error checking subscription:', error);
      return false;
    }
  }

  static async getSubscriptionData(): Promise<any> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedSubscription: any = null;
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          const { getFirestoreFunctions } = require('../services/unifiedAuthService');
          const { getApps, initializeApp } = require('firebase/app');
          const { firebaseConfig } = require('../config/firebaseConfig');
          const { AuthManager } = await import('./authManager');
          const firestoreMod = getFirestoreFunctions?.();
          if (!firestoreMod) {
            throw new Error('Firestore module unavailable');
          }
          const { doc, getDoc, getFirestore } = firestoreMod;
          const app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig);
          const db = getFirestore(app);
          
          if (db) {
            const user = await AuthManager.getSavedUser();
            const businessOwner = await AuthManager.getSavedBusinessOwner();
            const userId = user?.id || businessOwner?.id;
            
            if (userId) {
              const subscriptionDoc = await getDoc(doc(db, 'users', userId, 'subscription', 'current'));
              if (subscriptionDoc.exists()) {
                loadedSubscription = subscriptionDoc.data();
                loadedFromFirestore = true;
                console.log(`✅ TrialManager: Loaded subscription data from Firestore`);
                
                // Sync to local storage
                await ProductionStorage.setItem('bookapt_subscription', JSON.stringify(loadedSubscription));
              }
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  TrialManager: Could not load subscription from Firestore, trying local storage:', firestoreError);
        }
      }
      
      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
      const subscriptionData = await ProductionStorage.getItem('bookapt_subscription');
        if (subscriptionData) {
          loadedSubscription = JSON.parse(subscriptionData);
          console.log(`📋 TrialManager: Loaded subscription data from local storage`);
        }
      }
      
      return loadedSubscription;
    } catch (error) {
      console.error('Error getting subscription data:', error);
      return null;
    }
  }
}
